/* eslint-disable react/no-danger */
import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Text,
  Divider,
  ActionIcon,
  Button,
  Autocomplete,
  Group,
} from "@mantine/core";
import { IconTrash, IconPlus } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import type { AmendmentCommentContext } from "../../hooks/useAmendmentDiagnosticDraft";
import {
  TemplateSection,
  DocumentViewResponse,
} from "../../types/reportTemplate";
import { DocumentHeader } from "./DocumentHeader";
import { DiagnosisSection } from "./DiagnosisSection";
import { SignatureBlockSection } from "./SignatureBlockSection";
import { CptIcdCodesSection } from "./CptIcdCodesSection";
import { AddendumDocSection } from "./AddendumDocSection";
import { OrganMapSection, type OrganMapSpecimen } from "./OrganMapSection";
import type { OrganMapPhoto } from "./ReportPhotoContent";
import {
  REPORT_ORGAN_MAP_HEIGHT_PT,
  getReportPhotoReferenceWidth,
} from "./reportOrganMapLayout";
import { StainTableSection } from "./StainTableSection";
import { EditableSection, type EditableSectionHandle } from "./EditableSection";
import { CLINICAL_RICH_TEXT_CLASS } from "../../utils/clinicalRichText";
import RichTextDiagnosis, {
  DiagnosisMacroItem,
  VariableConfig,
} from "./RichTextDiagnosis";
import { isEmptyHtml, resolveRichTextForDisplay } from "../../utils/helpers";
import {
  useAddPathologistComment,
  useDeletePathologistComment,
} from "../../hooks/useWorkflows";
import {
  replaceSystemVariables,
  extractVariables,
} from "../../utils/grossTemplate";
import {
  getGrossCodeFragmentCountPatch,
  type FragmentCountQualifier,
} from "../../utils/fragmentCount";
import { sanitizeReportFooterHtml } from "../../utils/sanitize";
import { resolveEffectiveClientReportIdentity } from "../../utils/reportIdentity";
import { formatDiagnosisHeading } from "./diagnosisHeading";
import { ReportSupplementNotices } from "./ReportSupplementNotices";
import { reportSupplementEntriesOfType } from "./reportSupplementGroups";

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface SpecimenDiagnosisData {
  specimenId: string;
  macroCode?: string;
  diagnosisText: string;
  microscopicDescription: string;
  location?: string | null;
  category?: string | null;
  cptCode?: string | null;
  icdCode?: string | null;
  icdCodes?: string[];
  color?: string | null;
  sizeCm?: string | null;
  sizeMm?: string | null;
  consistency?: string | null;
}

interface SpecimenGrossingData {
  specimenId: string;
  grossCode?: string;
  grossCodeId?: string;
  grossDescription: string;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
  variableValues?: Record<string, string>;
}

interface Specimen {
  id: string;
  number: string;
  sourceName: string;
  // Enriched fields for variable resolution + gross template
  sourceId?: string | null;
  subsiteName?: string | null;
  findings?: string | null;
  collectionMethod?: string | null;
  blockCount?: number | null;
  // Organ map fields (from source.organMapTemplate and subsite positions)
  organMapTemplateId?: string | null;
  organMapTemplateName?: string | null;
  organMapImageUrl?: string | null;
  organMapWidth?: number | null;
  organMapHeight?: number | null;
  subsitePositionX?: number | null;
  subsitePositionY?: number | null;
}

interface CaseData {
  id?: string;
  caseNumber: string;
  clinicalHistoryNotes?: string;
  indications?: string;
  impressions?: string;
  isFinalized: boolean;
  signedOffAt?: string;
  dateReceived?: string;
  dateCollected?: string;
  procedure?: string;
  statusName?: string;
  physicianId?: string;
  sendingPracticeId?: string;
  surgeryCenterLabId?: string;
  patient?: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    medicalRecordNumber?: string;
  };
  physician?: {
    firstName?: string;
    lastName?: string;
    credentials?: string;
  };
  signedOffBy?: {
    fullName?: string;
    credentials?: string;
  };
}

interface PathologistCommentItem {
  id: string;
  commentText: string;
  createdAt: string;
  createdBy: { fullName: string; credentials: string | null } | null;
}

interface GrossCode {
  value: string;
  label: string;
  description?: string;
  scenarioDescription?: string;
  fragmentCount?: number | null;
  fragmentCountQualifier?: FragmentCountQualifier | null;
}

interface DocumentSectionProps {
  section: TemplateSection;
  documentViewData: DocumentViewResponse;
  caseData: CaseData;
  specimens: Specimen[];
  diagnosisDataMap: Map<string, SpecimenDiagnosisData>;
  grossingDataMap: Map<string, SpecimenGrossingData>;
  clinicalHistoryNotes: string;
  indications: string;
  impressions: string;
  pathologistComment: string;
  pathologistComments?: PathologistCommentItem[];
  amendmentCommentContext?: AmendmentCommentContext;
  frozenInterpretation?: string;
  isReadOnly: boolean;
  reviewSimulation?: boolean;
  onClinicalHistoryChange?: (value: string) => void;
  onIndicationsChange?: (value: string) => void;
  onImpressionsChange?: (value: string) => void;
  onCommentChange?: (value: string) => void;
  onDiagnosisChange?: (
    specimenId: string,
    field:
      | "diagnosisText"
      | "microscopicDescription"
      | "color"
      | "sizeCm"
      | "location",
    value: string
  ) => void;
  onGrossChange?: (specimenId: string, value: string) => void;
  onAddAddendum?: () => void;
  onAddAmendment?: () => void;
  onAddCorrection?: () => void;
  onCodesChange?: (type: "cpt" | "icd", displayCodes: string[]) => void;
  liveCptDisplay?: string[] | null;
  liveIcdDisplay?: string[] | null;
  onHeaderSaved?: () => void;
  /** Diagnosis macros for the macro toolbar on editable diagnosis sections */
  macros?: DiagnosisMacroItem[];
  /** Called when a macro is applied to a specimen's diagnosis (all fields) */
  onMacroApplied?: (specimenId: string, macro: DiagnosisMacroItem) => void;
  /** Gross codes for the gross template code input */
  grossCodes?: GrossCode[];
  /** Called when a gross template and its canonical fragment pair are applied */
  onGrossCodeApplied?: (
    specimenId: string,
    data: {
      grossCode: string;
      grossCodeId: string;
      grossDescription: string;
      fragmentCount: number | null;
      fragmentCountQualifier: FragmentCountQualifier | null;
    }
  ) => void;
  /** Called when a gross template variable value changes */
  onGrossVariableChange?: (
    specimenId: string,
    varName: string,
    value: string
  ) => void;
  /** Subsites keyed by sourceId — for the LOCATION variable chip on diagnosis */
  subsiteOptionsBySource?: Record<
    string,
    Array<{ value: string; label: string }>
  >;
  /** Diagnosis categories for organ map marker shape/color lookup */
  diagnosisCategories?: Array<{
    name: string;
    markerShape: string | null;
    markerColor: string | null;
  }>;
  /** 0-indexed page number when rendered inside the paginated preview. Used to switch
   * the header to its compact "continuation page" form on pages 2+. */
  pageIndex?: number;
  /** Selected photos rendered in the report media section. */
  reportPhotos?: OrganMapPhoto[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function stripVariablesAndHtml(text: string): string {
  if (!text) return "";
  const withoutVars = text.replace(/\{\{[^}]+\}\}/g, "");
  const doc = new DOMParser().parseFromString(withoutVars, "text/html");
  return (doc.body.textContent || "").trim();
}

function getDescriptionMarginTop(
  fragmentIndex: number | undefined,
  specimenIndex: number
): number | string {
  if (fragmentIndex !== undefined && fragmentIndex > 0) return 0;
  return specimenIndex === 0 ? 4 : "4pt";
}

// ─── Inline Diagnosis Code Row ─────────────────────────────────────────────

function DiagnosisCodeRow({
  specimenId,
  currentCode,
  macros,
  isReadOnly,
  inputRef,
  onApply,
  onFocusNext,
}: {
  specimenId: string;
  currentCode: string;
  macros: DiagnosisMacroItem[];
  isReadOnly: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  onApply: (specimenId: string, macro: DiagnosisMacroItem) => void;
  onFocusNext: () => void;
}) {
  const [inputVal, setInputVal] = useState(currentCode || "");
  const [hasError, setHasError] = useState(false);

  const suggestions = useMemo(() => {
    const q = inputVal.trim().toLowerCase();
    if (!q) return [];
    return macros
      .reduce<Array<{ macro: DiagnosisMacroItem; rank: number }>>((acc, m) => {
        const codeLC = m.code.toLowerCase();
        if (codeLC === q) acc.push({ macro: m, rank: 0 });
        else if (codeLC.startsWith(q)) acc.push({ macro: m, rank: 1 });
        else if (codeLC.includes(q)) acc.push({ macro: m, rank: 2 });
        else if (q.length >= 4) {
          const plain = stripVariablesAndHtml(m.diagnosisText);
          if (plain.toLowerCase().includes(q)) acc.push({ macro: m, rank: 3 });
        }
        return acc;
      }, [])
      .sort((a, b) => a.rank - b.rank)
      .slice(0, 15)
      .map(({ macro: m }) => ({ value: m.code, label: m.code }));
  }, [inputVal, macros]);

  const applyCode = useCallback(
    (code: string): boolean => {
      const macro = macros.find(
        (m) => m.code.toLowerCase() === code.toLowerCase()
      );
      if (!macro) {
        setHasError(true);
        return false;
      }
      setHasError(false);
      setInputVal(macro.code);
      onApply(specimenId, macro);
      return true;
    },
    [macros, onApply, specimenId]
  );

  if (isReadOnly) {
    if (!currentCode) return null;
    return (
      <Text size="xs" ff="monospace" fw={700} c="blue" mb={2}>
        {currentCode}
      </Text>
    );
  }

  return (
    <Autocomplete
      ref={inputRef}
      value={inputVal}
      onChange={(v) => {
        setInputVal(v.toUpperCase());
        setHasError(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Tab" || e.key === "Enter") {
          const ok = applyCode(inputVal);
          if (ok) {
            e.preventDefault();
            setTimeout(onFocusNext, 0);
          } else if (e.key === "Enter") {
            e.preventDefault();
          }
        }
      }}
      onBlur={() => {
        if (inputVal && inputVal !== currentCode) applyCode(inputVal);
      }}
      onOptionSubmit={(v) => {
        applyCode(v);
        setTimeout(onFocusNext, 0);
      }}
      data={suggestions}
      filter={({ options }) => options}
      renderOption={({ option }) => {
        const macro = macros.find((m) => m.code === option.value);
        const preview = macro ? stripVariablesAndHtml(macro.diagnosisText) : "";
        return (
          <Group gap="xs" wrap="nowrap">
            <Text size="xs" fw={700} ff="monospace" style={{ flexShrink: 0 }}>
              {option.value}
            </Text>
            {preview && (
              <Text size="xs" c="dimmed" lineClamp={1} style={{ flex: 1 }}>
                {preview}
              </Text>
            )}
          </Group>
        );
      }}
      comboboxProps={{ width: 450, position: "bottom-start" }}
      placeholder="Dx Code"
      error={hasError ? "Not found" : undefined}
      size="xs"
      style={{ width: 120 }}
      styles={{
        input: {
          fontFamily: "monospace",
          fontWeight: 600,
          textTransform: "uppercase",
        },
      }}
    />
  );
}

// ─── Inline Gross Template Code Row ───────────────────────────────────────────

function GrossCodeRow({
  specimenId,
  currentCode,
  grossCodes,
  specimen,
  caseData,
  isReadOnly,
  inputRef,
  onApply,
  onFocusNext,
}: {
  specimenId: string;
  currentCode: string;
  grossCodes: GrossCode[];
  specimen: Specimen;
  caseData: CaseData;
  isReadOnly: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  onApply: (
    specimenId: string,
    data: {
      grossCode: string;
      grossCodeId: string;
      grossDescription: string;
      fragmentCount: number | null;
      fragmentCountQualifier: FragmentCountQualifier | null;
    }
  ) => void;
  onFocusNext: () => void;
}) {
  const [inputVal, setInputVal] = useState(currentCode || "");
  const [hasError, setHasError] = useState(false);

  const suggestions = useMemo(() => {
    const q = inputVal.trim().toLowerCase();
    if (!q) return [];
    return grossCodes
      .reduce<Array<{ code: GrossCode; rank: number }>>((acc, m) => {
        const labelLC = m.label.toLowerCase();
        if (labelLC === q) acc.push({ code: m, rank: 0 });
        else if (labelLC.startsWith(q)) acc.push({ code: m, rank: 1 });
        else if (labelLC.includes(q)) acc.push({ code: m, rank: 2 });
        else if (q.length >= 4) {
          const plain = stripVariablesAndHtml(m.description || "");
          if (plain.toLowerCase().includes(q)) acc.push({ code: m, rank: 3 });
        }
        return acc;
      }, [])
      .sort((a, b) => a.rank - b.rank)
      .slice(0, 15)
      .map(({ code: m }) => ({ value: m.label, label: m.label }));
  }, [inputVal, grossCodes]);

  const buildDescription = useCallback(
    (gc: GrossCode): string => {
      const patientName = [
        caseData.patient?.firstName,
        caseData.patient?.lastName,
      ]
        .filter(Boolean)
        .join(" ");
      const context = {
        specimenLetter: specimen.number,
        specimenCount: undefined,
        sourceName: specimen.sourceName,
        subsiteName: specimen.subsiteName || undefined,
        collectionMethod: specimen.collectionMethod || undefined,
        blockCount: specimen.blockCount || undefined,
        cassetteCount: specimen.blockCount || undefined,
        patientName: patientName || undefined,
        caseNumber: caseData.caseNumber || undefined,
        dateCollected: caseData.dateCollected || undefined,
      };
      return replaceSystemVariables(gc.description || "", context);
    },
    [specimen, caseData]
  );

  const applyCode = useCallback(
    (code: string): boolean => {
      const gc = grossCodes.find(
        (m) => m.label.toLowerCase() === code.toLowerCase()
      );
      if (!gc) {
        setHasError(true);
        return false;
      }
      setHasError(false);
      setInputVal(gc.label);
      const desc = buildDescription(gc);
      const fragmentCountPatch = getGrossCodeFragmentCountPatch(
        gc.fragmentCount,
        gc.fragmentCountQualifier
      ) ?? { fragmentCount: null, fragmentCountQualifier: null };
      onApply(specimenId, {
        grossCode: gc.label,
        grossCodeId: gc.value,
        grossDescription: desc,
        ...fragmentCountPatch,
      });
      return true;
    },
    [grossCodes, buildDescription, onApply, specimenId]
  );

  if (isReadOnly) {
    if (!currentCode) return null;
    return (
      <Text size="xs" ff="monospace" fw={700} c="blue" mb={2}>
        {currentCode}
      </Text>
    );
  }

  return (
    <Autocomplete
      ref={inputRef}
      value={inputVal}
      onChange={(v) => {
        setInputVal(v.toUpperCase());
        setHasError(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Tab" || e.key === "Enter") {
          const ok = applyCode(inputVal);
          if (ok) {
            e.preventDefault();
            setTimeout(onFocusNext, 0);
          } else if (e.key === "Enter") {
            e.preventDefault();
          }
        }
      }}
      onBlur={() => {
        if (inputVal && inputVal !== currentCode) applyCode(inputVal);
      }}
      onOptionSubmit={(v) => {
        applyCode(v);
        setTimeout(onFocusNext, 0);
      }}
      data={suggestions}
      filter={({ options }) => options}
      renderOption={({ option }) => {
        const gc = grossCodes.find((m) => m.label === option.value);
        const preview = gc ? stripVariablesAndHtml(gc.description || "") : "";
        return (
          <Group gap="xs" wrap="nowrap">
            <Text size="xs" fw={700} ff="monospace" style={{ flexShrink: 0 }}>
              {option.value}
            </Text>
            {preview && (
              <Text size="xs" c="dimmed" lineClamp={1} style={{ flex: 1 }}>
                {preview}
              </Text>
            )}
          </Group>
        );
      }}
      placeholder="Gross Code"
      error={hasError ? "Not found" : undefined}
      size="xs"
      style={{ width: 130 }}
      styles={{
        input: {
          fontFamily: "monospace",
          fontWeight: 600,
          textTransform: "uppercase",
        },
      }}
    />
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export function DocumentSection({
  section,
  documentViewData,
  caseData,
  specimens,
  diagnosisDataMap,
  grossingDataMap,
  clinicalHistoryNotes,
  indications,
  impressions,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  pathologistComment: _pathologistComment,
  pathologistComments,
  amendmentCommentContext,
  frozenInterpretation,
  isReadOnly,
  reviewSimulation = false,
  onClinicalHistoryChange,
  onIndicationsChange,
  onImpressionsChange,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onCommentChange: _onCommentChange,
  onDiagnosisChange,
  onGrossChange,
  onAddAddendum,
  onAddAmendment,
  onAddCorrection,
  onCodesChange,
  liveCptDisplay,
  liveIcdDisplay,
  onHeaderSaved,
  macros,
  onMacroApplied,
  grossCodes,
  onGrossCodeApplied,
  onGrossVariableChange,
  subsiteOptionsBySource,
  diagnosisCategories,
  pageIndex = 0,
  reportPhotos,
}: DocumentSectionProps) {
  const [newCommentText, setNewCommentText] = useState("");
  const [showAddComment, setShowAddComment] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const addComment = useAddPathologistComment();
  const deleteComment = useDeletePathologistComment();

  // Ref maps for Tab/Enter focus advance — keyed by specimen ID
  const dxCodeRefs = useRef<Map<string, React.RefObject<HTMLInputElement>>>(
    new Map()
  );
  const grossCodeRefs = useRef<Map<string, React.RefObject<HTMLInputElement>>>(
    new Map()
  );
  const dxSectionRefs = useRef<
    Map<string, React.RefObject<EditableSectionHandle>>
  >(new Map());
  const grossSectionRefs = useRef<
    Map<string, React.RefObject<EditableSectionHandle>>
  >(new Map());

  // Ensure a ref exists for every specimen
  specimens.forEach((s) => {
    if (!dxCodeRefs.current.has(s.id)) {
      dxCodeRefs.current.set(s.id, { current: null });
    }
    if (!grossCodeRefs.current.has(s.id)) {
      grossCodeRefs.current.set(s.id, { current: null });
    }
    if (!dxSectionRefs.current.has(s.id)) {
      dxSectionRefs.current.set(s.id, { current: null });
    }
    if (!grossSectionRefs.current.has(s.id)) {
      grossSectionRefs.current.set(s.id, { current: null });
    }
  });

  const waitForRefAndFocus = useCallback(
    (specimenId: string, codeRefs: typeof dxCodeRefs, retries = 20) => {
      const tryFocus = (remaining: number) => {
        const input = codeRefs.current.get(specimenId)?.current;
        if (input) {
          input.focus();
        } else if (remaining > 0) {
          requestAnimationFrame(() => tryFocus(remaining - 1));
        }
      };
      requestAnimationFrame(() => tryFocus(retries));
    },
    []
  );

  const focusNextDxCode = useCallback(
    (currentIndex: number) => {
      const remaining = specimens.slice(currentIndex + 1);
      const nextEmpty = remaining.find((s) => {
        const diag = diagnosisDataMap.get(s.id);
        return isEmptyHtml(diag?.diagnosisText || "");
      });
      if (nextEmpty) {
        const inputRef = dxCodeRefs.current.get(nextEmpty.id);
        if (inputRef?.current) {
          inputRef.current.focus();
        } else {
          dxSectionRefs.current.get(nextEmpty.id)?.current?.open();
          waitForRefAndFocus(nextEmpty.id, dxCodeRefs);
        }
      }
    },
    [specimens, diagnosisDataMap, waitForRefAndFocus]
  );

  const focusNextGrossCode = useCallback(
    (currentIndex: number) => {
      const remaining = specimens.slice(currentIndex + 1);
      const nextEmpty = remaining.find((s) => {
        const gross = grossingDataMap.get(s.id);
        return isEmptyHtml(gross?.grossDescription || "");
      });
      if (nextEmpty) {
        const inputRef = grossCodeRefs.current.get(nextEmpty.id);
        if (inputRef?.current) {
          inputRef.current.focus();
        } else {
          grossSectionRefs.current.get(nextEmpty.id)?.current?.open();
          waitForRefAndFocus(nextEmpty.id, grossCodeRefs);
        }
      }
    },
    [specimens, grossingDataMap, waitForRefAndFocus]
  );

  // Initial focus lands on the first specimen still awaiting a diagnosis, so the
  // pathologist can type straight away instead of clicking into the report on
  // every case. The Dx Code input only exists once its EditableSection is open,
  // so the section is opened first — open() then drives the focus retry itself.
  // Switching to the Report view remounts this component, so a mount-time effect
  // covers both opening a case and activating the tab.
  const hasFocusedInitialDxCode = useRef(false);

  // A diagnosis section may be configured to render a single specimen, so the
  // report can mount several of these instances. Only the instance that actually
  // renders the target specimen may claim initial focus, otherwise each instance
  // would fight over it and the last one mounted would win.
  const initialFocusSpecimenIndex = useMemo(() => {
    const firstEmpty = specimens.findIndex((s) => {
      const diag = diagnosisDataMap.get(s.id);
      return isEmptyHtml(diag?.diagnosisText || "");
    });
    if (firstEmpty !== -1) return firstEmpty;
    return specimens.length > 0 ? 0 : -1;
  }, [specimens, diagnosisDataMap]);

  const focusInitialDxCode = useCallback(() => {
    const target = specimens[initialFocusSpecimenIndex];
    if (!target) return;
    const inputRef = dxCodeRefs.current.get(target.id);
    if (inputRef?.current) {
      inputRef.current.focus();
      return;
    }
    // The code input is mounted by opening the section; open() invokes
    // onEditingStart, which drives the ref-attach retry.
    dxSectionRefs.current.get(target.id)?.current?.open();
    waitForRefAndFocus(target.id, dxCodeRefs);
  }, [specimens, initialFocusSpecimenIndex, waitForRefAndFocus]);

  useEffect(() => {
    if (section.type !== "diagnosis" || isReadOnly) return;
    if (hasFocusedInitialDxCode.current) return;
    if (initialFocusSpecimenIndex === -1) return;

    // Only the per-specimen layout wires up Dx Code inputs; the grouped layout
    // has no code field to focus.
    if (!section.config?.perSpecimen) return;

    const configuredIndex = section.config?.specimenIndex;
    const rendersTarget =
      typeof configuredIndex !== "number" ||
      configuredIndex === initialFocusSpecimenIndex;
    if (!rendersTarget) return;

    hasFocusedInitialDxCode.current = true;
    focusInitialDxCode();
  }, [
    section.type,
    section.config?.perSpecimen,
    section.config?.specimenIndex,
    isReadOnly,
    initialFocusSpecimenIndex,
    focusInitialDxCode,
  ]);

  const { type, label, config } = section;
  const {
    template,
    facility,
    practice,
    addendums = [],
    cptCodes,
    icdCodes,
    disclaimers = {
      tcDisclaimer: null,
      pcDisclaimer: null,
      ihcGeneralComment: null,
    },
  } = documentViewData;

  const effectiveReportIdentity = resolveEffectiveClientReportIdentity(
    {
      caseNumber: caseData.caseNumber,
      facility: {
        name: facility.name,
        medicalDirector: null,
        address: facility.address,
        phone: facility.phone,
        fax: facility.fax,
        logoUrl: facility.logoUrl,
      },
      practice,
      ihcGeneralComment: disclaimers.ihcGeneralComment,
    },
    documentViewData.reportIdentity
  );
  const reportAddendums = useMemo(
    () => reportSupplementEntriesOfType(addendums, "addendum"),
    [addendums]
  );

  const physicianName = caseData.physician
    ? `${caseData.physician.firstName || ""} ${
        caseData.physician.lastName || ""
      }`.trim()
    : "";

  // Build variableConfig for a given specimen (used by the TipTap editor chips)
  const buildVariableConfig = useCallback(
    (s: Specimen): VariableConfig => {
      const diagData = diagnosisDataMap.get(s.id);
      const subsiteOptions = subsiteOptionsBySource?.[s.sourceId || ""] || [];
      const patientName = [
        caseData.patient?.firstName,
        caseData.patient?.lastName,
      ]
        .filter(Boolean)
        .join(" ");

      return {
        LOCATION: {
          options: subsiteOptions,
          value: diagData?.location ?? s.subsiteName ?? null,
          onChange: (val) => onDiagnosisChange?.(s.id, "location", val),
          readOnly: false,
        },
        location: {
          options: subsiteOptions,
          value: diagData?.location ?? s.subsiteName ?? null,
          onChange: (val) => onDiagnosisChange?.(s.id, "location", val),
          readOnly: false,
        },
        subsite: {
          options: subsiteOptions,
          value: diagData?.location ?? s.subsiteName ?? null,
          onChange: (val) => onDiagnosisChange?.(s.id, "location", val),
          readOnly: false,
        },
        specimen_letter: {
          options: [],
          value: s.number ?? null,
          onChange: () => {},
          readOnly: true,
        },
        source: {
          options: [],
          value: s.sourceName ?? null,
          onChange: () => {},
          readOnly: true,
        },
        collection_method: {
          options: [],
          value: s.collectionMethod ?? null,
          onChange: () => {},
          readOnly: true,
        },
        block_count: {
          options: [],
          value: s.blockCount != null ? String(s.blockCount) : null,
          onChange: () => {},
          readOnly: true,
        },
        case_number: {
          options: [],
          value: caseData.caseNumber ?? null,
          onChange: () => {},
          readOnly: true,
        },
        patient_name: {
          options: [],
          value: patientName || null,
          onChange: () => {},
          readOnly: true,
        },
        date_collected: {
          options: [],
          value: caseData.dateCollected ?? null,
          onChange: () => {},
          readOnly: true,
        },
        status: {
          options: [],
          value: caseData.statusName || null,
          onChange: () => {},
          readOnly: true,
        },
        color: {
          options: [],
          value: diagData?.color ?? null,
          onChange: (val) => onDiagnosisChange?.(s.id, "color", val),
          readOnly: false,
        },
        size_cm: {
          options: [],
          value: diagData?.sizeCm ?? null,
          onChange: (val) => onDiagnosisChange?.(s.id, "sizeCm", val),
          readOnly: false,
        },
      };
    },
    [diagnosisDataMap, subsiteOptionsBySource, caseData, onDiagnosisChange]
  );

  // Build case-level variableConfig (no specimen context)
  const buildCaseVariableConfig = useCallback((): VariableConfig => {
    const patientName = [
      caseData.patient?.firstName,
      caseData.patient?.lastName,
    ]
      .filter(Boolean)
      .join(" ");

    return {
      case_number: {
        options: [],
        value: caseData.caseNumber ?? null,
        onChange: () => {},
        readOnly: true,
      },
      patient_name: {
        options: [],
        value: patientName || null,
        onChange: () => {},
        readOnly: true,
      },
      date_collected: {
        options: [],
        value: caseData.dateCollected ?? null,
        onChange: () => {},
        readOnly: true,
      },
      status: {
        options: [],
        value: caseData.statusName || null,
        onChange: () => {},
        readOnly: true,
      },
    };
  }, [caseData]);

  const caseVariableConfig = useMemo(
    () => buildCaseVariableConfig(),
    [buildCaseVariableConfig]
  );
  const caseVars = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(caseVariableConfig).map(([k, v]) => [k, v.value])
      ),
    [caseVariableConfig]
  );

  const specimenDiagnoses = specimens.map((s) => {
    const vars = buildVariableConfig(s);
    const flatVars = Object.fromEntries(
      Object.entries(vars).map(([key, config]) => [key, config.value])
    );
    return {
      specimenId: s.id,
      specimenLabel: s.number,
      specimenSource: s.sourceName,
      specimenSubsite:
        diagnosisDataMap.get(s.id)?.location ?? s.subsiteName ?? null,
      findings: s.findings,
      // C-4756: Prioritize specimen collection method over case procedure (internal label leak)
      procedure:
        s.collectionMethod ||
        (caseData.procedure && !/GI\d* parts?/i.test(caseData.procedure)
          ? caseData.procedure
          : null),
      diagnosisText: resolveRichTextForDisplay(
        diagnosisDataMap.get(s.id)?.diagnosisText || "",
        flatVars
      ),
    };
  });

  // Build organ map specimen data from specimens + diagnosis data + category marker info
  const organMapSpecimens: OrganMapSpecimen[] = specimens.map((s) => {
    const diagData = diagnosisDataMap.get(s.id);
    const categoryName = diagData?.category || null;
    const catInfo = categoryName
      ? diagnosisCategories?.find((c) => c.name === categoryName)
      : null;
    return {
      specimenId: s.id,
      specimenLabel: s.number,
      sourceName: s.sourceName,
      organMapTemplateId: s.organMapTemplateId || null,
      organMapTemplateName: s.organMapTemplateName || null,
      organMapImageUrl: s.organMapImageUrl || null,
      organMapWidth: s.organMapWidth || null,
      organMapHeight: s.organMapHeight || null,
      subsiteName: s.subsiteName || null,
      positionX: s.subsitePositionX ?? null,
      positionY: s.subsitePositionY ?? null,
      categoryName,
      markerShape: catInfo?.markerShape || null,
      markerColor: catInfo?.markerColor || null,
    };
  });

  switch (type) {
    case "header":
      return (
        <DocumentHeader
          title={template.templateContent.title}
          facility={facility}
          practice={practice}
          patient={{
            firstName: caseData.patient?.firstName || "",
            lastName: caseData.patient?.lastName || "",
            dateOfBirth: caseData.patient?.dateOfBirth || null,
            medicalRecordNumber: caseData.patient?.medicalRecordNumber || null,
            sex: documentViewData.patient?.sex || null,
            age: documentViewData.patient?.age ?? null,
          }}
          caseInfo={{
            caseNumber: caseData.caseNumber,
            dateReceived: caseData.dateReceived || null,
            dateCollected: caseData.dateCollected || null,
            dateReported: documentViewData.dateReported || null,
            physicianName: documentViewData.physician?.name || physicianName,
            physicianId: caseData.physicianId || null,
            copyTo: documentViewData.copyTo || null,
            sendingPracticeId: caseData.sendingPracticeId || null,
            surgeryCenterLabId: caseData.surgeryCenterLabId || null,
          }}
          caseId={caseData.id}
          isReadOnly={isReadOnly || Boolean(amendmentCommentContext)}
          onSaved={onHeaderSaved}
          facilityLogoUrl={documentViewData.facility.logoUrl}
          practiceLogoUrl={documentViewData.practice.logoUrl}
          reportIdentity={effectiveReportIdentity}
          isContinuationPage={pageIndex > 0}
        />
      );

    case "report_supplement_notices":
      return (
        <ReportSupplementNotices
          entries={addendums}
          fragment={section.config?.reportSupplementFragment}
          correctionPlacement={section.config?.correctionPlacement}
        />
      );

    case "patient_info":
      return null;

    case "clinical_history": {
      const hasClinicalHistory = !isEmptyHtml(clinicalHistoryNotes);
      const hasIndications = !isEmptyHtml(indications);
      const hasImpressions = !isEmptyHtml(impressions);
      const hasAny = hasClinicalHistory || hasIndications || hasImpressions;
      if (!hasAny && isReadOnly) return null;
      // Per GI report spec: section label 12pt Bold, body 11pt Regular, sub-labels 11pt Bold,
      // line-height 1.15, Times New Roman, no letter-spacing. Body sits one line below the label.
      const sectionLabelStyle: React.CSSProperties = {
        fontFamily: "'Times New Roman', Times, serif",
        fontSize: "12pt",
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
        margin: 0,
      };
      const subLabelStyle: React.CSSProperties = {
        fontFamily: "'Times New Roman', Times, serif",
        fontSize: "11pt",
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
        margin: 0,
      };
      const bodyTextStyle: React.CSSProperties = {
        fontFamily: "'Times New Roman', Times, serif",
        fontSize: "11pt",
        fontWeight: 400,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
      };
      return (
        <Box mt={12} style={{ breakInside: "avoid", pageBreakInside: "avoid" }}>
          <Text style={sectionLabelStyle}>{label}</Text>
          {(hasClinicalHistory || !isReadOnly) && (
            <>
              {(hasIndications || hasImpressions || !isReadOnly) && (
                <Text style={{ ...subLabelStyle, marginTop: 4 }}>
                  Clinical History
                </Text>
              )}
              <EditableSection
                value={clinicalHistoryNotes}
                onChange={onClinicalHistoryChange || (() => {})}
                placeholder="Enter clinical history..."
                isReadOnly={isReadOnly}
                textStyle={bodyTextStyle}
                variableConfig={caseVariableConfig}
              />
            </>
          )}
          {(hasIndications || !isReadOnly) && (
            <>
              <Text style={{ ...subLabelStyle, marginTop: 4 }}>
                Indications
              </Text>
              <EditableSection
                value={indications}
                onChange={onIndicationsChange || (() => {})}
                placeholder="Enter indications..."
                isReadOnly={isReadOnly}
                textStyle={bodyTextStyle}
                variableConfig={caseVariableConfig}
              />
            </>
          )}
          {(hasImpressions || !isReadOnly) && (
            <>
              <Text style={{ ...subLabelStyle, marginTop: 4 }}>
                Impressions
              </Text>
              <EditableSection
                value={impressions}
                onChange={onImpressionsChange || (() => {})}
                placeholder="Enter impressions..."
                isReadOnly={isReadOnly}
                textStyle={bodyTextStyle}
                variableConfig={caseVariableConfig}
              />
            </>
          )}
        </Box>
      );
    }

    case "diagnosis": {
      const hasDiagnoses = specimenDiagnoses.some((d) => !!d.diagnosisText);
      if (!hasDiagnoses && isReadOnly) return null;

      const configuredIdx: number | undefined =
        typeof config?.specimenIndex === "number"
          ? config.specimenIndex
          : undefined;

      // Per GI report spec — DIAGNOSIS block typography:
      // label 12pt Bold, specimen letter 11pt Regular at x≈46.5pt,
      // site label 11pt Bold at x≈67.7pt, diagnosis 11pt Regular (mixed bold via rich text),
      // line-height 1.15, Times New Roman, no letter-spacing.
      const dxSectionLabelStyle: React.CSSProperties = {
        fontFamily: "'Times New Roman', Times, serif",
        fontSize: "12pt",
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
        margin: 0,
      };
      const dxSpecimenLetterStyle: React.CSSProperties = {
        fontFamily: "'Times New Roman', Times, serif",
        fontSize: "11pt",
        fontWeight: 400,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
        whiteSpace: "nowrap",
        flexShrink: 0,
        // Specimen letter sits ~10.5pt from margin
        paddingLeft: "10.5pt",
        // Width to the site-label tab stop (~31.7pt − 10.5pt = 21.2pt of letter cell)
        minWidth: "31.7pt",
      };
      const dxSiteLabelStyle: React.CSSProperties = {
        fontFamily: "'Times New Roman', Times, serif",
        fontSize: "11pt",
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
      };
      const dxBodyTextStyle: React.CSSProperties = {
        fontFamily: "'Times New Roman', Times, serif",
        fontSize: "11pt",
        fontWeight: 400,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
      };

      if (config?.perSpecimen) {
        const targetSpecimens =
          configuredIdx !== undefined
            ? [specimens[configuredIdx]].filter(Boolean)
            : specimens;

        return (
          <Box mt={config.showHeading !== false ? 12 : 0}>
            {config.showHeading !== false && (
              <Text style={dxSectionLabelStyle}>{label}</Text>
            )}
            {targetSpecimens.map((s, relativeIndex) => {
              const actualIndex: number = configuredIdx ?? relativeIndex;
              const diagData = diagnosisDataMap.get(s.id);
              const fragmentContent =
                config.reportNarrativeFragmentContentHtml ??
                diagData?.diagnosisText ??
                "";
              const showSpecimenLabel =
                config.reportNarrativeFragmentShowLabel !== false;
              // C-4756: Prioritize specimen collection method over case procedure (internal label leak)
              const procedureValue =
                s.collectionMethod ||
                (caseData.procedure && !/GI\d* parts?/i.test(caseData.procedure)
                  ? caseData.procedure
                  : "");
              const subsiteValue = diagData?.location ?? s.subsiteName ?? null;
              const heading = formatDiagnosisHeading({
                source: s.sourceName,
                subsite: subsiteValue,
                findings: s.findings,
                procedure: procedureValue,
              });
              const varConfig = buildVariableConfig(s);
              const dxRef = dxCodeRefs.current.get(s.id)!;
              const sectionRef = dxSectionRefs.current.get(s.id)!;

              return (
                <Box
                  key={`${s.id}:${
                    config.reportNarrativeFragmentIndex ?? "whole"
                  }`}
                  mt={getDescriptionMarginTop(
                    config.reportNarrativeFragmentIndex,
                    actualIndex
                  )}
                  style={{ breakInside: "avoid" }}
                >
                  {showSpecimenLabel && (
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 0,
                      }}
                    >
                      <span style={dxSpecimenLetterStyle}>
                        {String.fromCharCode(65 + actualIndex)}.
                      </span>
                      <span style={dxSiteLabelStyle}>{heading}</span>
                    </Box>
                  )}
                  <Box style={{ paddingLeft: "31.7pt" }}>
                    <EditableSection
                      ref={sectionRef}
                      value={fragmentContent}
                      onChange={(v) =>
                        onDiagnosisChange?.(s.id, "diagnosisText", v)
                      }
                      placeholder="Enter diagnosis..."
                      isReadOnly={isReadOnly}
                      textStyle={dxBodyTextStyle}
                      preserveRichTextLayout
                      onMacroApplied={(macro) => {
                        onMacroApplied?.(s.id, macro);
                      }}
                      variableConfig={varConfig}
                      codeIsEmpty={!diagData?.macroCode?.trim()}
                      onEditingStart={() =>
                        waitForRefAndFocus(s.id, dxCodeRefs)
                      }
                      codeInput={
                        macros && macros.length > 0 ? (
                          <DiagnosisCodeRow
                            specimenId={s.id}
                            currentCode={diagData?.macroCode || ""}
                            macros={macros}
                            isReadOnly={isReadOnly}
                            inputRef={dxRef}
                            onApply={(sid, macro) => {
                              onDiagnosisChange?.(
                                sid,
                                "diagnosisText",
                                macro.diagnosisText || ""
                              );
                              if (macro.microscopicDescription) {
                                onDiagnosisChange?.(
                                  sid,
                                  "microscopicDescription",
                                  macro.microscopicDescription
                                );
                              }
                              onMacroApplied?.(sid, macro);
                            }}
                            onFocusNext={() => focusNextDxCode(actualIndex)}
                          />
                        ) : undefined
                      }
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      }

      const diagnosisStartIndex: number = configuredIdx ?? 0;
      return (
        <DiagnosisSection
          label={label}
          diagnoses={specimenDiagnoses.slice(
            diagnosisStartIndex,
            configuredIdx !== undefined ? diagnosisStartIndex + 1 : undefined
          )}
          showHeading={config?.showHeading !== false}
          startIndex={diagnosisStartIndex}
        />
      );
    }

    case "microscopic_description": {
      if (
        isReadOnly &&
        specimens.every((specimen) =>
          isEmptyHtml(diagnosisDataMap.get(specimen.id)?.microscopicDescription)
        )
      ) {
        return null;
      }

      const microFontFamily = "'Times New Roman', Times, serif";
      const microSectionLabelStyle: React.CSSProperties = {
        fontFamily: microFontFamily,
        fontSize: "12pt",
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
        margin: 0,
      };
      const microSpecimenLetterStyle: React.CSSProperties = {
        fontFamily: microFontFamily,
        fontSize: "11pt",
        fontWeight: 400,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
        whiteSpace: "nowrap",
        flexShrink: 0,
        paddingLeft: "10.5pt",
        minWidth: "31.7pt",
      };
      const microBodyStyle: React.CSSProperties = {
        fontFamily: microFontFamily,
        fontSize: "11pt",
        fontWeight: 400,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
      };

      const isCombined = config?.combined;
      if (isCombined) {
        const combinedText =
          config?.reportNarrativeFragmentContentHtml ??
          Array.from(diagnosisDataMap.values())
            .map((d) => d.microscopicDescription)
            .filter((value) => !isEmptyHtml(value))
            .join("\n\n");

        const resolvedHtml = resolveRichTextForDisplay(combinedText, caseVars);

        return (
          <Box
            mt={config?.showHeading !== false ? 12 : 0}
            style={{ breakInside: "avoid", pageBreakInside: "avoid" }}
          >
            {config?.showHeading !== false && (
              <Text style={microSectionLabelStyle}>{label}</Text>
            )}
            <Box
              mt={config?.showHeading !== false ? 4 : 0}
              className={CLINICAL_RICH_TEXT_CLASS}
              style={microBodyStyle}
              dangerouslySetInnerHTML={{ __html: resolvedHtml }}
            />
          </Box>
        );
      }

      if (config?.perSpecimen) {
        const configuredIndex =
          typeof config.specimenIndex === "number"
            ? config.specimenIndex
            : undefined;
        const targetSpecimens =
          configuredIndex === undefined
            ? specimens
            : [specimens[configuredIndex]].filter(Boolean);
        return (
          <Box mt={config.showHeading !== false ? 12 : 0}>
            {config.showHeading !== false && (
              <Text style={microSectionLabelStyle}>{label}</Text>
            )}
            {targetSpecimens.map((s, relativeIndex) => {
              const index = configuredIndex ?? relativeIndex;
              const diagData = diagnosisDataMap.get(s.id);
              const hasMicro = !isEmptyHtml(diagData?.microscopicDescription);
              if (!hasMicro && isReadOnly) return null;
              const varConfig = buildVariableConfig(s);
              const fragmentContent =
                (config.reportNarrativeFragmentContentHtml ??
                  diagData?.microscopicDescription) ||
                "";
              const showSpecimenLabel =
                config.reportNarrativeFragmentShowLabel !== false;
              const marginTop = getDescriptionMarginTop(
                config.reportNarrativeFragmentIndex,
                index
              );
              return (
                <Box
                  key={s.id}
                  mt={marginTop}
                  style={{
                    breakInside: "avoid",
                    display: "flex",
                    alignItems: "baseline",
                    gap: 0,
                  }}
                >
                  <span style={microSpecimenLetterStyle}>
                    {showSpecimenLabel
                      ? `${String.fromCharCode(65 + index)}.`
                      : ""}
                  </span>
                  <Box style={{ flex: 1 }}>
                    <EditableSection
                      value={fragmentContent}
                      onChange={(v) =>
                        onDiagnosisChange?.(s.id, "microscopicDescription", v)
                      }
                      placeholder="Enter microscopic description..."
                      isReadOnly={isReadOnly}
                      inlineStart
                      variableConfig={varConfig}
                      textStyle={microBodyStyle}
                      preserveRichTextLayout
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      }

      return null;
    }

    case "gross_description": {
      const grossFontFamily = "'Times New Roman', Times, serif";
      const grossSectionLabelStyle: React.CSSProperties = {
        fontFamily: grossFontFamily,
        fontSize: "12pt",
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
        margin: 0,
      };
      const grossSpecimenLetterStyle: React.CSSProperties = {
        fontFamily: grossFontFamily,
        fontSize: "11pt",
        fontWeight: 400,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
        whiteSpace: "nowrap",
        flexShrink: 0,
        paddingLeft: "10.5pt",
        minWidth: "31.7pt",
      };
      const grossBodyStyle: React.CSSProperties = {
        fontFamily: grossFontFamily,
        fontSize: "11pt",
        fontWeight: 400,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
      };

      const isCombined = config?.combined;
      if (isCombined) {
        const combinedText =
          config?.reportNarrativeFragmentContentHtml ??
          Array.from(grossingDataMap.values())
            .map((d) => d.grossDescription)
            .filter((value) => !isEmptyHtml(value))
            .join("\n\n");
        if (isEmptyHtml(combinedText) && isReadOnly) return null;

        const resolvedHtml = resolveRichTextForDisplay(combinedText, caseVars);

        return (
          <Box
            mt={config?.showHeading !== false ? 12 : 0}
            style={{ breakInside: "avoid", pageBreakInside: "avoid" }}
          >
            {config?.showHeading !== false && (
              <Text style={grossSectionLabelStyle}>{label}</Text>
            )}
            <Box
              mt={config?.showHeading !== false ? 4 : 0}
              style={grossBodyStyle}
              dangerouslySetInnerHTML={{ __html: resolvedHtml }}
            />
          </Box>
        );
      }

      if (config?.perSpecimen) {
        const configuredIndex =
          typeof config.specimenIndex === "number"
            ? config.specimenIndex
            : undefined;
        const targetSpecimens =
          configuredIndex === undefined
            ? specimens
            : [specimens[configuredIndex]].filter(Boolean);
        return (
          <Box mt={config.showHeading !== false ? 12 : 0}>
            {config.showHeading !== false && (
              <Text style={grossSectionLabelStyle}>{label}</Text>
            )}
            {targetSpecimens.map((s, relativeIndex) => {
              const index = configuredIndex ?? relativeIndex;
              const grossData = grossingDataMap.get(s.id);
              const hasGross = !isEmptyHtml(grossData?.grossDescription);
              if (!hasGross && isReadOnly) return null;
              const grossRef = grossCodeRefs.current.get(s.id)!;
              const grossSectionRef = grossSectionRefs.current.get(s.id)!;
              const fragmentContent =
                (config.reportNarrativeFragmentContentHtml ??
                  grossData?.grossDescription) ||
                "";
              const showSpecimenLabel =
                config.reportNarrativeFragmentShowLabel !== false;
              const marginTop = getDescriptionMarginTop(
                config.reportNarrativeFragmentIndex,
                index
              );

              return (
                <Box
                  key={s.id}
                  mt={marginTop}
                  style={{
                    breakInside: "avoid",
                    display: "flex",
                    alignItems: "baseline",
                    gap: 0,
                  }}
                >
                  <span style={grossSpecimenLetterStyle}>
                    {showSpecimenLabel
                      ? `${String.fromCharCode(65 + index)}.`
                      : ""}
                  </span>
                  <Box style={{ flex: 1 }}>
                    {(() => {
                      const remainingVars = extractVariables(fragmentContent);
                      const varValues = grossData?.variableValues || {};
                      const grossVariableConfig: Record<
                        string,
                        {
                          options: { label: string; value: string }[];
                          value: string | null;
                          onChange: (val: string) => void;
                        }
                      > = {};
                      remainingVars.forEach((varName) => {
                        grossVariableConfig[varName] = {
                          options: [],
                          value: varValues[varName] ?? null,
                          onChange: (val: string) =>
                            onGrossVariableChange?.(s.id, varName, val),
                        };
                      });
                      return (
                        <EditableSection
                          ref={grossSectionRef}
                          value={fragmentContent}
                          onChange={(v) => onGrossChange?.(s.id, v)}
                          placeholder="Enter gross description..."
                          isReadOnly={isReadOnly}
                          inlineStart
                          variableConfig={
                            remainingVars.length > 0
                              ? grossVariableConfig
                              : undefined
                          }
                          codeIsEmpty={!grossData?.grossCode?.trim()}
                          onEditingStart={() =>
                            waitForRefAndFocus(s.id, grossCodeRefs)
                          }
                          codeInput={
                            grossCodes && grossCodes.length > 0 ? (
                              <GrossCodeRow
                                specimenId={s.id}
                                currentCode={grossData?.grossCode || ""}
                                grossCodes={grossCodes}
                                specimen={s}
                                caseData={caseData}
                                isReadOnly={isReadOnly}
                                inputRef={grossRef}
                                onApply={(sid, data) => {
                                  onGrossChange?.(sid, data.grossDescription);
                                  onGrossCodeApplied?.(sid, data);
                                }}
                                onFocusNext={() => focusNextGrossCode(index)}
                              />
                            ) : undefined
                          }
                          textStyle={grossBodyStyle}
                        />
                      );
                    })()}
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      }

      return null;
    }

    case "comments": {
      const hasComments = pathologistComments && pathologistComments.length > 0;
      if (!hasComments && isReadOnly) return null;

      const handleAddComment = async () => {
        if (!caseData.id || !newCommentText.trim()) return;
        try {
          if (amendmentCommentContext)
            await amendmentCommentContext.addComment(newCommentText);
          else
            await addComment.mutateAsync({
              caseId: caseData.id,
              commentText: newCommentText,
            });
          setNewCommentText("");
          setShowAddComment(false);
        } catch {
          notifications.show({
            title: "Error",
            message: "Failed to save comment",
            color: "red",
          });
        }
      };

      const handleDeleteComment = async (commentId: string) => {
        if (!caseData.id) return;
        setDeletingId(commentId);
        try {
          if (amendmentCommentContext)
            await amendmentCommentContext.deleteComment(commentId);
          else
            await deleteComment.mutateAsync({ caseId: caseData.id, commentId });
        } catch {
          notifications.show({
            title: "Error",
            message: "Failed to delete comment",
            color: "red",
          });
        } finally {
          setDeletingId(null);
        }
      };

      // Per GI report spec — COMMENTS:
      // label 12pt Bold, body 11pt Regular, Times New Roman, line-height 1.15, no border.
      const commentsLabelStyle: React.CSSProperties = {
        fontFamily: "'Times New Roman', Times, serif",
        fontSize: "12pt",
        fontWeight: 700,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
        margin: 0,
      };
      const commentsBodyStyle: React.CSSProperties = {
        fontFamily: "'Times New Roman', Times, serif",
        fontSize: "11pt",
        fontWeight: 400,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
      };
      const configuredCommentIndex =
        typeof config?.commentIndex === "number"
          ? config.commentIndex
          : undefined;
      const targetComments =
        configuredCommentIndex === undefined
          ? pathologistComments
          : [pathologistComments?.[configuredCommentIndex]].filter(Boolean);
      return (
        <Box
          mt={config?.showHeading !== false ? 12 : 0}
          className="report-comments"
          style={{ breakInside: "avoid", pageBreakInside: "avoid" }}
        >
          {config?.showHeading !== false && (
            <Box
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Text style={commentsLabelStyle}>{label}</Text>
              {!isReadOnly && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  leftSection={<IconPlus size={12} />}
                  onClick={() => setShowAddComment((v) => !v)}
                  style={{ marginBottom: 2 }}
                >
                  Add comment
                </Button>
              )}
            </Box>
          )}

          {hasComments
            ? targetComments?.map((c, idx) => (
                <Box
                  key={`${c?.id}:${
                    config?.reportNarrativeFragmentIndex ?? "whole"
                  }`}
                  mt={idx === 0 && config?.showHeading !== false ? 4 : 0}
                  mb={4}
                  style={{ display: "flex", alignItems: "flex-start", gap: 6 }}
                >
                  <Box style={{ flex: 1 }}>
                    <Box
                      className="report-comment-body"
                      dangerouslySetInnerHTML={{
                        __html:
                          config?.reportNarrativeFragmentContentHtml ??
                          c?.commentText ??
                          "",
                      }}
                      style={commentsBodyStyle}
                    />
                  </Box>
                  {!isReadOnly && c && (
                    <ActionIcon
                      size="xs"
                      color="red"
                      variant="subtle"
                      loading={deletingId === c.id}
                      onClick={() => handleDeleteComment(c.id)}
                      style={{ flexShrink: 0, marginTop: 2 }}
                    >
                      <IconTrash size={12} />
                    </ActionIcon>
                  )}
                </Box>
              ))
            : !showAddComment && (
                <Text size="sm" c="dimmed" fs="italic">
                  No comments
                </Text>
              )}

          {!isReadOnly && showAddComment && (
            <Box mt={8}>
              <RichTextDiagnosis
                value={newCommentText}
                onChange={setNewCommentText}
                placeholder="Enter comment..."
                minHeight={80}
              />
              <Box
                mt={6}
                style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
              >
                <Button
                  size="xs"
                  variant="subtle"
                  color="gray"
                  onClick={() => {
                    setShowAddComment(false);
                    setNewCommentText("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  onClick={handleAddComment}
                  loading={addComment.isPending}
                  disabled={isEmptyHtml(newCommentText)}
                >
                  Save comment
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    case "frozen_section": {
      const hasFrozen =
        frozenInterpretation && !isEmptyHtml(frozenInterpretation);
      if (!hasFrozen && isReadOnly) return null;
      return (
        <Box mt={16} style={{ breakInside: "avoid", pageBreakInside: "avoid" }}>
          <Text
            fw={700}
            size="sm"
            mb={6}
            style={{ letterSpacing: 1, borderBottom: "1px solid #333" }}
          >
            {label}
          </Text>
          <EditableSection
            value={frozenInterpretation || ""}
            onChange={() => {}}
            placeholder="No frozen interpretation recorded"
            isReadOnly
          />
        </Box>
      );
    }

    case "organ_map":
    case "diagnostic_map":
      return (
        <OrganMapSection
          specimens={organMapSpecimens}
          imageHeight={REPORT_ORGAN_MAP_HEIGHT_PT}
          hideLabels
          reportPhotos={reportPhotos}
          reportMode
          mediaRowIndex={config?.mediaRowIndex}
        />
      );

    case "stain_table":
      return (
        <StainTableSection
          label={label}
          ihcResults={documentViewData.ihcResults}
          specialStainResults={documentViewData.specialStainResults}
          tableColumns={config?.tableColumns}
        />
      );

    case "signature_block":
      return (
        <SignatureBlockSection
          isSigned={caseData.isFinalized}
          reviewSimulation={reviewSimulation}
          signedBy={
            caseData.signedOffBy?.fullName ||
            documentViewData.pathologistInfo.name ||
            undefined
          }
          signedAt={caseData.signedOffAt}
          credentials={
            caseData.signedOffBy?.credentials ||
            documentViewData.pathologistInfo.credentials ||
            caseData.physician?.credentials
          }
          signatureImageUrl={documentViewData.signatureImageUrl || undefined}
        />
      );

    case "addendum": {
      const rendersLastAddendum =
        (typeof config?.addendumIndex !== "number" ||
          config.addendumIndex === reportAddendums.length - 1) &&
        config?.addendumShowSignature !== false;
      return (
        <AddendumDocSection
          reportPhotos={reportPhotos}
          photoWidth={getReportPhotoReferenceWidth(organMapSpecimens)}
          addendums={
            typeof config?.addendumIndex === "number"
              ? [reportAddendums[config.addendumIndex]].filter(Boolean)
              : reportAddendums
          }
          cptCodes={cptCodes}
          icdCodes={icdCodes}
          specimenCount={specimens.length}
          showCodes={config?.showCodes === true}
          fragment={
            typeof config?.addendumFragmentContentHtml === "string"
              ? {
                  contentHtml: config.addendumFragmentContentHtml,
                  showHeading: config.addendumShowHeading !== false,
                  showSignature: config.addendumShowSignature !== false,
                  photoRowIndex: config.addendumPhotoRowIndex,
                }
              : undefined
          }
          isReadOnly={isReadOnly}
          onAddAddendum={rendersLastAddendum ? onAddAddendum : undefined}
          onAddAmendment={rendersLastAddendum ? onAddAmendment : undefined}
          onAddCorrection={rendersLastAddendum ? onAddCorrection : undefined}
        />
      );
    }

    case "cpt_icd_codes":
      return (
        <CptIcdCodesSection
          cptCodes={cptCodes}
          icdCodes={icdCodes}
          isReadOnly={isReadOnly}
          onCodesChange={onCodesChange}
          liveCptDisplay={liveCptDisplay}
          liveIcdDisplay={liveIcdDisplay}
          specimenCount={specimens.length}
        />
      );

    case "disclaimers": {
      const disclaimerStyle: React.CSSProperties = {
        fontFamily: "'Times New Roman', Times, serif",
        fontSize: "6pt",
        fontWeight: 400,
        lineHeight: 1.15,
        letterSpacing: 0,
        color: "#000000",
        margin: 0,
      };
      const resolvedFooterHtml = effectiveReportIdentity.footerHtml;
      const safeFooterHtml = sanitizeReportFooterHtml(resolvedFooterHtml || "");
      if (safeFooterHtml) {
        return (
          <Box mt={12} style={disclaimerStyle}>
            <style>{`
              .report-disclaimers,
              .report-disclaimers .report-footer {
                display: flex;
                flex-direction: column;
                gap: 0;
              }
              .report-disclaimers p {
                margin: 0;
                padding: 0;
                line-height: 1.15;
              }
            `}</style>
            <Box
              className="report-disclaimers"
              dangerouslySetInnerHTML={{ __html: safeFooterHtml }}
            />
          </Box>
        );
      }
      const { tcDisclaimer, pcDisclaimer, ihcGeneralComment } = disclaimers;
      const safePcDisclaimer = sanitizeReportFooterHtml(pcDisclaimer || "")
        ? pcDisclaimer
        : null;
      if (!tcDisclaimer && !safePcDisclaimer && !ihcGeneralComment) return null;
      return (
        <Box mt={12}>
          {tcDisclaimer && <Box style={disclaimerStyle}>{tcDisclaimer}</Box>}
          {safePcDisclaimer && (
            <Box style={disclaimerStyle}>{safePcDisclaimer}</Box>
          )}
          {ihcGeneralComment && (
            <Box style={disclaimerStyle}>{ihcGeneralComment}</Box>
          )}
        </Box>
      );
    }

    case "quality_control_review":
      return (
        <Box mt={16} style={{ breakInside: "avoid" }}>
          <Divider mb={8} />
          <Text size="xs" c="dimmed">
            This report has been reviewed for quality control.
          </Text>
        </Box>
      );

    default:
      return null;
  }
}
